'use client';

import type { CategoryData } from 'src/types/category-data';
import type { TagData } from 'src/types/tag-data';
import { useMemo } from 'react';
import { createColumnConfigHelper, DataTableFilter, useDataTableFilters } from '@savvy-kit/templates/data-table-filter';
import { isNil, uniqBy } from 'es-toolkit';
import { STATE_CODE_TO_NAME } from 'src/constants/us-states';
import { graphql } from 'src/gql';

import type { ColumnOption, FiltersState, ResultCountConfig } from '@savvy-kit/templates/data-table-filter';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import type { AdvisorInfo } from '../../types/advisor-info';
import type { SavedClientFilter } from '../hooks/use-saved-client-filters';
import { CategoryIndicator } from '../../tags/category-indicator';
import { useClientsUrlState, useClientsUrlStateDispatch } from '../utils/url-state-hooks';

const AdvisorHouseholdStatesQuery = graphql(/* GraphQL */ `
  query AdvisorHouseholdStates {
    advisorHouseholdStates
  }
`);

const SERVICE_CALENDAR_COLUMN_ID = 'serviceCalendar';
const LAST_CONTACT_DAYS_SINCE_COLUMN_ID = 'lastContactDaysSince';
const ADVISOR_COLUMN_ID = 'advisor';
const HOUSEHOLD_TAGS_COLUMN_ID = 'householdTags';
const HOUSEHOLD_TAG_CATEGORIES_COLUMN_ID = 'householdTagCategories';
const STATE_COLUMN_ID = 'state';

const SERVICE_CALENDAR_OPTIONS: ColumnOption[] = [
  { value: 'reviewDue', label: 'Review due' },
  { value: 'servicesDue', label: 'Services due' },
];

const LAST_CONTACT_DAYS_SINCE_OPTIONS: ColumnOption[] = [
  { value: '60', label: '60+ days' },
  { value: '90', label: '90+ days' },
  { value: '180', label: '180+ days' },
  { value: '365', label: '365+ days' },
];

type ClientFilterRow = {
  serviceCalendar: string[];
  lastContactDaysSince: string[];
  advisor: string[];
  householdTags: string[];
  householdTagCategories: string[];
  state: string[];
};

type DeriveBooleanFilterValueParams = {
  includedValues: readonly string[];
  excludedValues: readonly string[];
  value: string;
};

function deriveBooleanFilterValue({ includedValues, excludedValues, value }: DeriveBooleanFilterValueParams) {
  if (includedValues.includes(value)) {
    return true;
  }

  if (excludedValues.includes(value)) {
    return false;
  }

  return undefined;
}

function stateToFilters(
  serviceCalendar: string[],
  serviceCalendarExcluded: string[],
  lastContactDaysSince: string[],
  lastContactDaysSinceExcluded: string[],
  advisor: string[],
  advisorExcluded: string[],
  householdTags: string[],
  householdTagsExcluded: string[],
  householdTagCategories: string[],
  householdTagCategoriesExcluded: string[],
  stateValues: string[],
  stateValuesExcluded: string[],
): FiltersState {
  const filters: FiltersState = [];
  if (serviceCalendar.length > 0 || serviceCalendarExcluded.length > 0) {
    filters.push({
      columnId: SERVICE_CALENDAR_COLUMN_ID,
      type: 'multiOption',
      operator: serviceCalendar.length > 1 ? 'include any of' : 'include',
      values: [...serviceCalendar],
      excludedValues: serviceCalendarExcluded.length > 0 ? [...serviceCalendarExcluded] : undefined,
    });
  }
  if (advisor.length > 0 || advisorExcluded.length > 0) {
    filters.push({
      columnId: ADVISOR_COLUMN_ID,
      type: 'multiOption',
      operator: advisor.length > 1 ? 'include any of' : 'include',
      values: [...advisor],
      excludedValues: advisorExcluded.length > 0 ? [...advisorExcluded] : undefined,
    });
  }
  if (lastContactDaysSince.length > 0 || lastContactDaysSinceExcluded.length > 0) {
    filters.push({
      columnId: LAST_CONTACT_DAYS_SINCE_COLUMN_ID,
      type: 'multiOption',
      operator: lastContactDaysSince.length > 1 ? 'include any of' : 'include',
      values: [...lastContactDaysSince],
      excludedValues: lastContactDaysSinceExcluded.length > 0 ? [...lastContactDaysSinceExcluded] : undefined,
    });
  }
  if (householdTags.length > 0 || householdTagsExcluded.length > 0) {
    filters.push({
      columnId: HOUSEHOLD_TAGS_COLUMN_ID,
      type: 'multiOption',
      // operator is not used for rendering or server filtering on value-negation columns;
      // set sensible default regardless of whether only excluded tags are present
      operator: householdTags.length > 1 ? 'include any of' : 'include',
      values: [...householdTags],
      excludedValues: householdTagsExcluded.length > 0 ? [...householdTagsExcluded] : undefined,
    });
  }
  if (householdTagCategories.length > 0 || householdTagCategoriesExcluded.length > 0) {
    filters.push({
      columnId: HOUSEHOLD_TAG_CATEGORIES_COLUMN_ID,
      type: 'multiOption',
      operator: householdTagCategories.length > 1 ? 'include any of' : 'include',
      values: [...householdTagCategories],
      excludedValues: householdTagCategoriesExcluded.length > 0 ? [...householdTagCategoriesExcluded] : undefined,
    });
  }
  if (stateValues.length > 0 || stateValuesExcluded.length > 0) {
    filters.push({
      columnId: STATE_COLUMN_ID,
      type: 'multiOption',
      operator: stateValues.length > 1 ? 'include any of' : 'include',
      values: [...stateValues],
      excludedValues: stateValuesExcluded.length > 0 ? [...stateValuesExcluded] : undefined,
    });
  }
  return filters;
}

function filtersToState(filters: FiltersState): {
  serviceCalendar: string[];
  serviceCalendarExcluded: string[];
  lastContactDaysSince: string[];
  lastContactDaysSinceExcluded: string[];
  advisor: string[];
  advisorExcluded: string[];
  householdTags: string[];
  householdTagsExcluded: string[];
  householdTagCategories: string[];
  householdTagCategoriesExcluded: string[];
  state: string[];
  stateExcluded: string[];
} {
  const serviceCalendarFilter = filters.find((f) => f.columnId === SERVICE_CALENDAR_COLUMN_ID);
  const lastContactDaysSinceFilter = filters.find((f) => f.columnId === LAST_CONTACT_DAYS_SINCE_COLUMN_ID);
  const advisorFilter = filters.find((f) => f.columnId === ADVISOR_COLUMN_ID);
  const householdTagsFilter = filters.find((f) => f.columnId === HOUSEHOLD_TAGS_COLUMN_ID);
  const householdTagCategoriesFilter = filters.find((f) => f.columnId === HOUSEHOLD_TAG_CATEGORIES_COLUMN_ID);
  const stateFilter = filters.find((f) => f.columnId === STATE_COLUMN_ID);

  return {
    serviceCalendar:
      serviceCalendarFilter?.type === 'multiOption'
        ? serviceCalendarFilter.values.filter((v): v is string => typeof v === 'string')
        : [],
    serviceCalendarExcluded:
      serviceCalendarFilter?.type === 'multiOption'
        ? (serviceCalendarFilter.excludedValues ?? []).filter((v): v is string => typeof v === 'string')
        : [],
    lastContactDaysSince:
      lastContactDaysSinceFilter?.type === 'multiOption'
        ? lastContactDaysSinceFilter.values.filter((v): v is string => typeof v === 'string')
        : [],
    lastContactDaysSinceExcluded:
      lastContactDaysSinceFilter?.type === 'multiOption'
        ? (lastContactDaysSinceFilter.excludedValues ?? []).filter((v): v is string => typeof v === 'string')
        : [],
    advisor:
      advisorFilter?.type === 'multiOption'
        ? advisorFilter.values.filter((v): v is string => typeof v === 'string')
        : [],
    advisorExcluded:
      advisorFilter?.type === 'multiOption'
        ? (advisorFilter.excludedValues ?? []).filter((v): v is string => typeof v === 'string')
        : [],
    householdTags:
      householdTagsFilter?.type === 'multiOption'
        ? householdTagsFilter.values.filter((v): v is string => typeof v === 'string')
        : [],
    householdTagsExcluded:
      householdTagsFilter?.type === 'multiOption'
        ? (householdTagsFilter.excludedValues ?? []).filter((v): v is string => typeof v === 'string')
        : [],
    householdTagCategories:
      householdTagCategoriesFilter?.type === 'multiOption'
        ? householdTagCategoriesFilter.values.filter((v): v is string => typeof v === 'string')
        : [],
    householdTagCategoriesExcluded:
      householdTagCategoriesFilter?.type === 'multiOption'
        ? (householdTagCategoriesFilter.excludedValues ?? []).filter((v): v is string => typeof v === 'string')
        : [],
    state:
      stateFilter?.type === 'multiOption' ? stateFilter.values.filter((v): v is string => typeof v === 'string') : [],
    stateExcluded:
      stateFilter?.type === 'multiOption'
        ? (stateFilter.excludedValues ?? []).filter((v): v is string => typeof v === 'string')
        : [],
  };
}

type ClientsFilterDropdownProps = {
  readonly advisorInfo?: AdvisorInfo | null;
  readonly displayPrimaryAdvisorFilter: boolean;
  readonly tags?: readonly TagData[];
  readonly categories?: readonly CategoryData[];
  readonly savedFilterProps?: {
    readonly atLimit: boolean;
    readonly activePresetName: string | null;
    readonly isModified: boolean;
    readonly hasFilters: boolean;
    readonly existingNames: readonly string[];
    readonly savedFilters: readonly SavedClientFilter[];
    readonly onSave: (name: string) => void;
    readonly onUpdate: (id: string) => void;
  };
  readonly resultCount?: ResultCountConfig;
};

export function ClientsFilterDropdown({
  advisorInfo,
  displayPrimaryAdvisorFilter,
  tags,
  categories,
  savedFilterProps,
  resultCount,
}: ClientsFilterDropdownProps) {
  const urlState = useClientsUrlState();
  const dispatchUrl = useClientsUrlStateDispatch();

  const { data: statesData } = useQueryWithErrorLogging(AdvisorHouseholdStatesQuery);
  const stateOptions = useMemo<ColumnOption[]>(
    () =>
      (statesData?.advisorHouseholdStates ?? []).map((code) => ({
        value: code,
        label: STATE_CODE_TO_NAME[code] ? `${code} — ${STATE_CODE_TO_NAME[code]}` : code,
      })),
    [statesData],
  );

  const advisorOptions = useMemo<ColumnOption[]>(() => {
    if (!displayPrimaryAdvisorFilter || isNil(advisorInfo) || advisorInfo.advisorGroups === null) return [];

    const advisors: { id: string; firstName: string; lastName: string }[] = [];

    if (advisorInfo.viewAdvisorGroupClients) {
      advisorInfo.advisorGroups.forEach((group) => {
        group.members.forEach((member) => {
          if (member.user.role === 'ADVISOR') {
            advisors.push({ id: member.user.id, firstName: member.user.firstName, lastName: member.user.lastName });
          }
        });
      });
    } else {
      advisors.push({
        id: advisorInfo.user.id,
        firstName: advisorInfo.user.firstName,
        lastName: advisorInfo.user.lastName,
      });
    }

    return uniqBy(advisors, (a) => a.id).map((a) => ({ value: a.id, label: `${a.firstName} ${a.lastName}` }));
  }, [advisorInfo, displayPrimaryAdvisorFilter]);

  const tagOptions = useMemo<ColumnOption[]>(
    () =>
      [...(tags ?? [])]
        .sort((a, b) => {
          const catA = a.categoryId ?? '';
          const catB = b.categoryId ?? '';
          if (catA !== catB) return catA.localeCompare(catB);
          return a.name.localeCompare(b.name);
        })
        .map((t) => ({
          value: t.id,
          label: t.name,
          icon:
            t.hexColorCode !== null || (t.emoji !== null && t.emoji !== '') || t.isSmart === true ? (
              <CategoryIndicator hexColorCode={t.hexColorCode} emoji={t.emoji} smart={t.isSmart === true} />
            ) : undefined,
        })),
    [tags],
  );

  const categoryOptions = useMemo<ColumnOption[]>(
    () =>
      [...(categories ?? [])]
        .filter(
          (c): c is CategoryData & { name: string } =>
            c.name !== null && c.organizationHouseholdTags.some((t) => t.householdCount > 0),
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => ({
          value: c.id,
          label: c.name,
          icon:
            c.hexColorCode !== null || (c.emoji !== null && c.emoji !== '') ? (
              <CategoryIndicator hexColorCode={c.hexColorCode} emoji={c.emoji} />
            ) : undefined,
        })),
    [categories],
  );

  const currentServiceCalendar = useMemo(() => {
    const values: string[] = [];
    if (urlState.reviewDue) values.push('reviewDue');
    if (urlState.servicesDue) values.push('servicesDue');
    return values;
  }, [urlState.reviewDue, urlState.servicesDue]);
  const currentServiceCalendarExcluded = useMemo(() => {
    const values: string[] = [];
    if (urlState.reviewDue === false) values.push('reviewDue');
    if (urlState.servicesDue === false) values.push('servicesDue');
    return values;
  }, [urlState.reviewDue, urlState.servicesDue]);

  const currentAdvisor = useMemo(() => urlState.advisor ?? [], [urlState.advisor]);
  const currentAdvisorExcluded = useMemo(() => urlState.advisorExcluded ?? [], [urlState.advisorExcluded]);
  const currentLastContactDaysSince = useMemo(
    () => urlState.lastContactDaysSince ?? [],
    [urlState.lastContactDaysSince],
  );
  const currentLastContactDaysSinceExcluded = useMemo(
    () => urlState.lastContactDaysSinceExcluded ?? [],
    [urlState.lastContactDaysSinceExcluded],
  );
  const currentHouseholdTags = useMemo(() => urlState.householdTags ?? [], [urlState.householdTags]);
  const currentHouseholdTagsExcluded = useMemo(
    () => urlState.householdTagsExcluded ?? [],
    [urlState.householdTagsExcluded],
  );
  const currentHouseholdTagCategories = useMemo(
    () => urlState.householdTagCategories ?? [],
    [urlState.householdTagCategories],
  );
  const currentHouseholdTagCategoriesExcluded = useMemo(
    () => urlState.householdTagCategoriesExcluded ?? [],
    [urlState.householdTagCategoriesExcluded],
  );
  const currentStateValues = useMemo(() => urlState.primaryAddressStates ?? [], [urlState.primaryAddressStates]);
  const currentStateValuesExcluded = useMemo(
    () => urlState.primaryAddressStatesExcluded ?? [],
    [urlState.primaryAddressStatesExcluded],
  );

  const filters = useMemo(
    () =>
      stateToFilters(
        currentServiceCalendar,
        currentServiceCalendarExcluded,
        currentLastContactDaysSince,
        currentLastContactDaysSinceExcluded,
        currentAdvisor,
        currentAdvisorExcluded,
        currentHouseholdTags,
        currentHouseholdTagsExcluded,
        currentHouseholdTagCategories,
        currentHouseholdTagCategoriesExcluded,
        currentStateValues,
        currentStateValuesExcluded,
      ),
    [
      currentServiceCalendar,
      currentServiceCalendarExcluded,
      currentLastContactDaysSince,
      currentLastContactDaysSinceExcluded,
      currentAdvisor,
      currentAdvisorExcluded,
      currentHouseholdTags,
      currentHouseholdTagsExcluded,
      currentHouseholdTagCategories,
      currentHouseholdTagCategoriesExcluded,
      currentStateValues,
      currentStateValuesExcluded,
    ],
  );

  const columnsConfig = useMemo(() => {
    const dtf = createColumnConfigHelper<ClientFilterRow>();
    return [
      dtf
        .multiOption()
        .id(SERVICE_CALENDAR_COLUMN_ID)
        .accessor((row) => row.serviceCalendar)
        .displayName('Service Calendar')
        .options([...SERVICE_CALENDAR_OPTIONS])
        .build(),
      dtf
        .multiOption()
        .id(LAST_CONTACT_DAYS_SINCE_COLUMN_ID)
        .accessor((row) => row.lastContactDaysSince)
        .displayName('Last contact')
        .options([...LAST_CONTACT_DAYS_SINCE_OPTIONS])
        .build(),
      ...(displayPrimaryAdvisorFilter && advisorOptions.length > 0
        ? [
            dtf
              .multiOption()
              .id(ADVISOR_COLUMN_ID)
              .accessor((row) => row.advisor)
              .displayName('Primary Advisor')
              .options(advisorOptions)
              .build(),
          ]
        : []),
      ...(tagOptions.length > 0
        ? [
            dtf
              .multiOption()
              .id(HOUSEHOLD_TAGS_COLUMN_ID)
              .accessor((row) => row.householdTags)
              .displayName('Tags')
              .options(tagOptions)
              .supportsValueNegation()
              .build(),
          ]
        : []),
      ...(categoryOptions.length > 0
        ? [
            dtf
              .multiOption()
              .id(HOUSEHOLD_TAG_CATEGORIES_COLUMN_ID)
              .accessor((row) => row.householdTagCategories)
              .displayName('Tag categories')
              .options(categoryOptions)
              .supportsValueNegation()
              .build(),
          ]
        : []),
      ...(stateOptions.length > 0
        ? [
            dtf
              .multiOption()
              .id(STATE_COLUMN_ID)
              .accessor((row) => row.state)
              .displayName('Primary location')
              .options(stateOptions)
              .build(),
          ]
        : []),
    ];
  }, [advisorOptions, tagOptions, categoryOptions, stateOptions, displayPrimaryAdvisorFilter]);

  const optionsMap = useMemo(
    () => ({
      [SERVICE_CALENDAR_COLUMN_ID]: [...SERVICE_CALENDAR_OPTIONS],
      [LAST_CONTACT_DAYS_SINCE_COLUMN_ID]: [...LAST_CONTACT_DAYS_SINCE_OPTIONS],
      ...(displayPrimaryAdvisorFilter ? { [ADVISOR_COLUMN_ID]: advisorOptions } : {}),
      ...(tagOptions.length > 0 ? { [HOUSEHOLD_TAGS_COLUMN_ID]: tagOptions } : {}),
      ...(categoryOptions.length > 0 ? { [HOUSEHOLD_TAG_CATEGORIES_COLUMN_ID]: categoryOptions } : {}),
      ...(stateOptions.length > 0 ? { [STATE_COLUMN_ID]: stateOptions } : {}),
    }),
    [advisorOptions, tagOptions, categoryOptions, stateOptions, displayPrimaryAdvisorFilter],
  );

  const onFiltersChange = useMemo(
    () => (next: FiltersState | ((prev: FiltersState) => FiltersState)) => {
      const nextFilters = typeof next === 'function' ? next(filters) : next;
      const {
        serviceCalendar,
        serviceCalendarExcluded,
        lastContactDaysSince,
        lastContactDaysSinceExcluded,
        advisor,
        advisorExcluded,
        householdTags,
        householdTagsExcluded,
        householdTagCategories,
        householdTagCategoriesExcluded,
        state,
        stateExcluded,
      } = filtersToState(nextFilters);
      const reviewDueValue = deriveBooleanFilterValue({
        includedValues: serviceCalendar,
        excludedValues: serviceCalendarExcluded,
        value: 'reviewDue',
      });
      const servicesDueValue = deriveBooleanFilterValue({
        includedValues: serviceCalendar,
        excludedValues: serviceCalendarExcluded,
        value: 'servicesDue',
      });
      dispatchUrl({
        type: 'setClientFilters',
        reviewDue: reviewDueValue,
        servicesDue: servicesDueValue,
        lastContactDaysSince,
        lastContactDaysSinceExcluded,
        advisor,
        advisorExcluded,
        householdTags,
        householdTagsExcluded,
        householdTagCategories,
        householdTagCategoriesExcluded,
        primaryAddressStates: state.length > 0 ? state : undefined,
        primaryAddressStatesExcluded: stateExcluded.length > 0 ? stateExcluded : undefined,
      });
    },
    [filters, dispatchUrl],
  );

  const {
    columns,
    filters: hookFilters,
    actions,
    strategy,
  } = useDataTableFilters({
    strategy: 'server',
    data: [],
    columnsConfig,
    filters,
    onFiltersChange,
    options: optionsMap,
  });

  const savedViews = !isNil(savedFilterProps)
    ? {
        atLimit: savedFilterProps.atLimit,
        activeViewName: savedFilterProps.activePresetName,
        isModified: savedFilterProps.isModified,
        hasFilters: savedFilterProps.hasFilters,
        existingNames: [...savedFilterProps.existingNames],
        savedViews: savedFilterProps.savedFilters.map((f) => ({ id: f.id, name: f.name })),
        onSave: savedFilterProps.onSave,
        onUpdate: savedFilterProps.onUpdate,
      }
    : undefined;

  return (
    <DataTableFilter
      columns={columns}
      filters={hookFilters}
      actions={actions}
      strategy={strategy}
      filterValueVariant="compact"
      showClearFilterButton={false}
      searchableColumnIds={[
        ADVISOR_COLUMN_ID,
        HOUSEHOLD_TAGS_COLUMN_ID,
        HOUSEHOLD_TAG_CATEGORIES_COLUMN_ID,
        STATE_COLUMN_ID,
      ]}
      savedViews={savedViews}
      resultCount={resultCount}
    />
  );
}
